"use client";

import * as React from "react";
import Icon from "@/components/library/Icon";
import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";

// Context to pass onOpenChange and blurBackdrop to DialogContent for the custom overlay
const DialogContext = React.createContext<{
  onOpenChange?: (open: boolean) => void;
  blurBackdrop?: boolean;
}>({});

// Custom Dialog that defaults to modal={false} to allow nested popovers/comboboxes
const Dialog = ({
  modal = false,
  onOpenChange,
  blurBackdrop = false,
  ...props
}: DialogPrimitive.DialogProps & { blurBackdrop?: boolean }) => {
  const contextValue = React.useMemo(
    () => ({ onOpenChange, blurBackdrop }),
    [onOpenChange, blurBackdrop]
  );

  return (
    <DialogContext.Provider value={contextValue}>
      <DialogPrimitive.Root
        modal={modal}
        onOpenChange={onOpenChange}
        {...props}
      />
    </DialogContext.Provider>
  );
};

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "z-50 fixed inset-0 bg-black/80 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Custom overlay component that works with modal={false}
const DialogBackdrop = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    onClose?: () => void;
    blurBackdrop?: boolean;
  }
>(({ className, onClose, blurBackdrop, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "z-50 fixed inset-0 bg-black/80 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      blurBackdrop && "backdrop-blur-sm",
      className
    )}
    onClick={onClose}
    aria-hidden="true"
    {...props}
  />
));
DialogBackdrop.displayName = "DialogBackdrop";

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  /**
   * Optional buttons (or other interactive elements) rendered immediately to
   * the left of the built-in close button in the top-right corner.
   */
  headerActions?: React.ReactNode;
  showCloseButton?: boolean;
};

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      style,
      onInteractOutside,
      onCloseAutoFocus,
      headerActions,
      showCloseButton = true,
      ...props
    },
    ref
  ) => {
  const { onOpenChange, blurBackdrop } = React.useContext(DialogContext);

  const handleCloseAutoFocus = (e: Event) => {
    onCloseAutoFocus?.(e);
    // Radix can leave pointer-events: none on body after close when the dialog was
    // opened from another overlay (dropdown, context menu, etc.).
    // https://github.com/radix-ui/primitives/issues/1241
    document.body.style.pointerEvents = "";
  };

  return (
    <DialogPortal>
      {/* Custom backdrop that works with modal={false} */}
      <DialogBackdrop
        onClose={() => onOpenChange?.(false)}
        blurBackdrop={blurBackdrop}
      />
      <DialogPrimitive.Content
        ref={ref}
        data-dialog-content
        onCloseAutoFocus={handleCloseAutoFocus}
        onInteractOutside={
          onInteractOutside ?? ((e) => e.preventDefault())
        }
        style={{
          border: "1px solid rgb(var(--200-color))",
          maxHeight: "calc(100dvh - 2rem)",
          ...style,
        }}
        className={cn(
          // Positioning and transforms
          "top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-50 fixed",
          // Sizing - base grid layout (can be overridden via style prop).
          // Cap height to the viewport and scroll internally so no dialog can
          // grow taller than the screen. Components that manage their own
          // header/footer/scroll regions override this with overflow-hidden.
          "gap-4 grid w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto",
          // Appearance
          "bg-background shadow-lg p-6 sm:rounded-lg",
          // Animations
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
          "duration-200",
          className
        )}
        {...props}
      >
        {children}
        {(headerActions || showCloseButton) && (
          <div className="top-4 right-4 absolute flex items-center gap-1">
            {headerActions}
            {showCloseButton && (
              <DialogPrimitive.Close className="data-[state=open]:bg-accent opacity-70 hover:opacity-100 rounded-sm focus:outline-none focus:ring-2 focus:ring-ring ring-offset-background focus:ring-offset-2 data-[state=open]:text-muted-foreground transition-opacity disabled:pointer-events-none">
                <Icon icon="xmark" className="w-4 h-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </div>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 sm:text-left text-center",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex sm:flex-row flex-col-reverse sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "font-semibold text-lg leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
